import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import {
  getMutationQueue,
  addToMutationQueue,
  removeMutationFromQueue,
  clearMutationQueue,
  type OfflineMutation,
} from "@/lib/offlineStorage";
import { useOnlineStatus } from "./useOffline";

interface SyncState {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncError: string | null;
}

export function useOfflineMutationQueue() {
  const isOnline = useOnlineStatus();
  const queryClient = useQueryClient();
  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    pendingCount: 0,
    lastSyncError: null,
  });
  const isSyncingRef = useRef(false);

  // Load pending count on mount
  useEffect(() => {
    getMutationQueue().then((q) =>
      setSyncState((prev) => ({ ...prev, pendingCount: q.length }))
    );
  }, []);

  // Queue a mutation for offline execution
  const queueMutation = useCallback(
    async (mutation: Omit<OfflineMutation, "id" | "timestamp">) => {
      const entry = await addToMutationQueue(mutation);
      setSyncState((prev) => ({
        ...prev,
        pendingCount: prev.pendingCount + 1,
      }));
      return entry;
    },
    []
  );

  // Process the queue — called when back online
  const processQueue = useCallback(async () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    setSyncState((prev) => ({ ...prev, isSyncing: true, lastSyncError: null }));

    const queue = await getMutationQueue();
    let failedCount = 0;

    for (const mutation of queue) {
      try {
        switch (mutation.method) {
          case "POST":
            await api.post(mutation.url, mutation.body);
            break;
          case "PUT":
            await api.put(mutation.url, mutation.body);
            break;
          case "DELETE":
            await api.delete(mutation.url);
            break;
        }
        await removeMutationFromQueue(mutation.id);
      } catch (error) {
        failedCount++;
        console.error(`Failed to sync mutation: ${mutation.description}`, error);
      }
    }

    // Invalidate cached queries to refresh data
    await queryClient.invalidateQueries({ queryKey: ["contacts"] });

    const remaining = await getMutationQueue();
    setSyncState({
      isSyncing: false,
      pendingCount: remaining.length,
      lastSyncError:
        failedCount > 0
          ? `${failedCount} change${failedCount > 1 ? "s" : ""} failed to sync`
          : null,
    });
    isSyncingRef.current = false;
  }, [queryClient]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && syncState.pendingCount > 0 && !isSyncingRef.current) {
      processQueue();
    }
  }, [isOnline, syncState.pendingCount, processQueue]);

  const clearQueue = useCallback(async () => {
    await clearMutationQueue();
    setSyncState((prev) => ({ ...prev, pendingCount: 0 }));
  }, []);

  return {
    isOnline,
    syncState,
    queueMutation,
    processQueue,
    clearQueue,
  };
}
